import { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  UserCog,
  Building2,
  Gift,
  Bell,
  LogOut,
  Truck,
  FileSliders,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { sidenavStyle } from "../../styles/layout";
import { NavLink, useLocation } from "react-router-dom";
import LogoutModel from "./logoutModel";
import { ApiConfig } from "@shared/Config/config";
import { CommonServices } from "../../services/commonServices";
import { apiHandler } from "@shared/Functions/apiHandler";
import { EventSourcePolyfill } from 'event-source-polyfill';

const Sidebar = ({ }) => {
  const location = useLocation();
  const role = sessionStorage.getItem("userType");

  const menuItems = [
    // Common For Fuel Station & Refinery
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      link: "dashboard",
      roles: ["fuel_station", "refinery"],
    },
    {
      id: "profile",
      label: "Profile",
      icon: UserCog,
      link: "profile",
      roles: ["fuel_station", "refinery"],
    },
    {
      id: "products",
      label: "Products",
      icon: Package,
      link: "products",
      roles: ["fuel_station", "refinery"],
    },
    {
      id: "Services",
      label: "Services",
      icon: FileSliders,
      link: "Services",
      roles: ["fuel_station"],
    },
    {
      id: "orders",
      label: "Orders",
      icon: ShoppingCart,
      link: "orders",
      roles: ["fuel_station"],
    },
    {
      id: "b2b",
      label: "Refinery Orders(B2B) ",
      icon: Building2,
      link: "b2b",
      roles: ["refinery", "fuel_station"],
    },
    // { id: "b2b-place-order", label: "Place Order", icon: Building2, link: "place-order", roles: ["fuel_station"] },

    // Fuel Station Only
    {
      id: "promos",
      label: "Promo Codes",
      icon: Gift,
      link: "promos",
      roles: ["fuel_station"],
    },

    // {
    //   id: "loyalty",
    //   label: "Loyalty Cards",
    //   icon: CreditCard,
    //   link: "loyalty",
    //   roles: ["fuel_station"],
    // },

    // Refinery Only
    {
      id: "driver",
      label: "Driver",
      icon: Truck,
      link: "driver",
      roles: ["refinery"],
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: Bell,
      link: "notifications",
      roles: ["fuel_station", "refinery"],
    },
  ];
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const classes = sidenavStyle(theme);
  const [profileData, setProfileData] = useState({});
  const [notificationCount, setNotificationCount] = useState(0);

  const getVendorProfile = async () => {
    const res = await CommonServices.getVendorProfile();
    apiHandler(res);
    if (res?.status === 200) {
      setProfileData(res?.data?.data);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const source = new EventSourcePolyfill(
      `${ApiConfig.baseUrl}/fuel_vendor/notificationCountStream`,
      {
        headers: {
          Authorization: `${token}`,
        },
        withCredentials: true, // include cookies if needed
      }
    );
    source.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setNotificationCount(Number(data?.data?.count) || 0);
    };
    source.onerror = (err) => {
      console.error('EventSource Error:', err);
    };
    return () => source.close();
  }, []);

  useEffect(() => {
    getVendorProfile();
  }, []);

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        flex: 1, // take available width from parent
        height: "100vh",
        "& .MuiDrawer-paper": {
          position: "relative", // important for flex
          width: "100%", // match parent width
          boxSizing: "border-box",
          borderRight: "1px solid #e0dddc",
        },
      }}
    >
      {/* Header */}
      <Box sx={classes.logoDiv}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box 
            component={"img"}
            src={profileData.logo} 
            alt="Logo" 
            sx={classes.logo} 
          />
          <Box>
            <Typography sx={classes.titleHeading}>
              {" "}
              {profileData.vendorName}{" "}
            </Typography>
            <Typography sx={classes.subtitle}>
              {" "}
              {role === "fuel_station" ? "Fuel Station" : "Refinery"} 
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Navigation */}
      <Box flex={1} p={1} sx={classes.sidenavBody}>
        <List>
          {menuItems
            .filter((item) => item.roles.includes(role))
            .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.includes(item.link);

              return (
                <NavLink to={`${item.link}`} key={item.id}>
                  <ListItemButton
                    sx={isActive ? classes.active : classes.inactive}
                  >
                    <ListItemIcon
                      sx={isActive ? classes.iconActive : classes.iconInactive}
                    >
                      <Icon size={20} />
                    </ListItemIcon>

                    {/* Stack ensures label on left, count on right */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <ListItemText
                        primary={item.label}
                        sx={classes.sidenavLinkText}
                      />

                      {item.id === "notifications" && notificationCount > 0 && (
                        <Box sx={classes.notifyCount}>
                          {notificationCount > 99 ? "99+" : notificationCount}
                        </Box>
                      )}
                    </Stack>
                  </ListItemButton>
                </NavLink>
              );
            })}
        </List>

        <Divider />

        <Box p={2}>
          <Box sx={classes.versionDiv}>
            <Typography variant="body2" align="center">
              {ApiConfig?.ver_refinery}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LogOut size={18} />}
            sx={classes.versionDiv}
            onClick={() => setOpenDialog(true)}
          >
            Sign Out
          </Button>
        </Box>
      </Box>

      <LogoutModel openDialog={openDialog} setOpenDialog={setOpenDialog} />
    </Drawer>
  );
};

export default Sidebar;
